import React from 'react';
import { Target, BookOpen, BarChart3, Cpu, TrendingUp, Award, Flame } from 'lucide-react';

// Six-step flow sourced from www.rudvan.online's "How It Works" section
// (2026-08-07), replacing the previous five-step version with that page's
// more specific copy.
const STEPS = [
  { number: 1, icon: Target, title: 'Start', description: 'Take a diagnostic test to understand your baseline' },
  { number: 2, icon: BookOpen, title: 'Practice', description: 'Solve AI-generated questions at the perfect difficulty' },
  { number: 3, icon: BarChart3, title: 'Analyze', description: 'Get detailed performance metrics and insights' },
  { number: 4, icon: Cpu, title: 'Adapt', description: 'AI creates a personalized study plan based on the analysis' },
  { number: 5, icon: TrendingUp, title: 'Improve', description: 'Target weak areas with focused practice' },
  { number: 6, icon: Award, title: 'Master', description: 'Build the accuracy and confidence to be ready for the real exam' },
];

function StepCard({ number, title, description, icon: Icon, isDarkTheme }) {
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';
  const cardBg = isDarkTheme ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';

  return (
    <div className={`rounded-card border ${cardBg} p-6 h-full`}>
      <div className="relative w-fit mb-4">
        <div className="w-14 h-14 rounded-full bg-brand-gradient flex items-center justify-center shadow-brand-md">
          <Icon className="text-white" size={22} />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-brand-blue font-bold text-xs flex items-center justify-center shadow-brand-sm">
          {number}
        </span>
      </div>
      <h4 className={`font-bold ${textColor} text-lg mb-1`}>{title}</h4>
      <p className={`${textMuted} text-sm leading-relaxed`}>{description}</p>
    </div>
  );
}

export default function HowItWorksSection({ isDarkTheme, onLoginClick }) {
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';

  return (
    <section id="how-it-works" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-16">
        <h2 className={`font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${textColor}`}>
          <span className="bg-brand-gradient bg-clip-text text-transparent tracking-wide">
            ADAPT. PRACTICE. MASTER.
          </span>
        </h2>
        <p className={`text-lg md:text-xl ${textMuted}`}>Your journey from novice to expert in six steps</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
        {STEPS.map((step) => (
          <StepCard key={step.number} isDarkTheme={isDarkTheme} {...step} />
        ))}
      </div>

      <div className="text-center">
        <p className={`text-lg font-semibold ${textColor} mb-6`}>Start your journey today</p>
        <button
          onClick={onLoginClick}
          className="inline-flex items-center gap-3 px-8 py-4 rounded-btn bg-brand-blue hover:bg-brand-blue-hover text-white font-bold text-lg transition-all duration-300 shadow-brand-btn hover:scale-[1.02]"
        >
          <Flame size={22} />
          Begin Your Prep
        </button>
      </div>
    </section>
  );
}
